import { useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import { SEARCH_OPEN_TOFU_MIGRATION_QUEUE_SUGGESTIONS } from "shared/Stack/OpenTofuMigration/gql";

import {
  MIGRATION_QUEUE_EXCLUDED_FILTER_FIELDS,
  MIGRATION_QUEUE_FILTER_VARIABLES,
  MIGRATION_QUEUE_SUGGESTIONS_DICTIONARY,
} from "./constants";

const useOpenTofuMigrationSearchQueueSuggestions = () => {
  const { filterVariables } = useFiltersVariables(MIGRATION_QUEUE_FILTER_VARIABLES);
  const { searchInput, predicates } = filterVariables;

  const { data, loading, previousData } = usePolledQuery(
    SEARCH_OPEN_TOFU_MIGRATION_QUEUE_SUGGESTIONS,
    {
      variables: {
        input: {
          fields: null,
          predicates,
          fullTextSearch: searchInput,
        },
      },
      pollingGroup: PollingIntervalGroups.Lists,
    }
  );

  const cachedFiltersData = useCachedFilterFields(
    data?.openTofuMigrationQueueSearchSuggestions?.fields
  );

  const sortOptions = useMemo(
    (): SortOption[] =>
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => ({
          value: field.name,
          label: MIGRATION_QUEUE_SUGGESTIONS_DICTIONARY[field.name] || field.name,
        })),
    [cachedFiltersData]
  );

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: MIGRATION_QUEUE_SUGGESTIONS_DICTIONARY,
    excludedFilterFields: MIGRATION_QUEUE_EXCLUDED_FILTER_FIELDS,
  });

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    loading,
    filteredCount:
      data?.openTofuMigrationQueueSearchSuggestions?.filteredCount ??
      previousData?.openTofuMigrationQueueSearchSuggestions?.filteredCount ??
      0,
  };
};

export default useOpenTofuMigrationSearchQueueSuggestions;
